import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
  signal
} from '@angular/core';
import {Router} from '@angular/router';

import {NoteAttachmentsDaoService} from '@q9elements/ui-kit/attachments';
import {
  OrgInfo,
  PermissionsService,
  REF_MODEL_STORE,
  RefModelList,
  RefModelNode,
  RefModelStoreService,
  SalesForceData,
  TakeUntilComponentDestroy,
  constants
} from '@q9elements/ui-kit/common';
import {
  DependenciesService,
  RefModelAttachmentsDaoService,
  RefModelSidebarDaoService,
  RefModelSidebarService
} from '@q9elements/ui-kit/refmodels';

import {forkJoin} from 'rxjs';
import {filter, finalize, take} from 'rxjs/operators';

import {DeleteNodeBaseDialogService} from '../editor-interface/components/extended/components/delete-node-base-dialog/delete-node-base-dialog.service';
import {NodeRemoveEmitterService} from '../editor-interface/services/node-remove-emitter.service';
import {UpdateTreeService} from '../services/update-tree.service';
import {InternalDialogService} from './services/internal-dialog.service';

@Component({
  selector: 'q9rme-internal-node-sidebar',
  templateUrl: './internal-sidebar.component.html',
  styleUrls: ['./internal-sidebar.component.scss']
})
export class InternalSidebarComponent extends TakeUntilComponentDestroy implements OnChanges {
  private permissionsService = inject(PermissionsService);
  private refModelSidebarService = inject(RefModelSidebarService);
  private refModelSidebarDaoService = inject(RefModelSidebarDaoService);
  private refModelNotesDaoService = inject(NoteAttachmentsDaoService);
  private refModelAttachmentDaoService = inject(RefModelAttachmentsDaoService);
  private dependenciesService = inject(DependenciesService);
  private deleteNodeDialogService = inject(DeleteNodeBaseDialogService);
  private nodeRemoveEmitterService = inject(NodeRemoveEmitterService);
  private internalDialogService = inject(InternalDialogService);
  private updateTreeService = inject(UpdateTreeService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private rmeStore = inject(RefModelStoreService);
  private dependenciesData = signal({});

  public loading = signal(false);

  public selectedNode = this.refModelSidebarService.refModelNode;
  public editDescriptionEnabled: boolean = false;
  public documentationTabCount = this.refModelSidebarService.documentationTabCount;
  public changesTabCount = this.refModelSidebarService.changesTabCount;
  public dependenciesTabCount = this.refModelSidebarService.dependenciesTabCount;
  public stakeholdersCount = this.refModelSidebarService.stakeholdersCount;
  public displayTemplate: string;

  private selectedTabIndex: number;
  private previousNodeId: string;

  @Input() refModel: SalesForceData;
  @Input() orgInfo: OrgInfo;
  @Input() node: RefModelNode;
  @Input() disableClose = true;
  @Input() opened = true;
  @Input() isDependencyTree = false;

  @Output() makeNodeRootClicked = new EventEmitter<void>();

  ngOnChanges() {
    this.setSidebarData();
  }

  private setSidebarData() {
    this.refModelSidebarService.orgInfo.set(this.orgInfo);
    this.refModelSidebarService.refModelMeta.set(this.refModel);
    this.refModelSidebarService.refModelNode.set(this.node as RefModelNode);
    this.displayTemplate = this.getNodeType(this.node, this.refModel);
    this.getUserRefModels();
    this.editDescriptionEnabled = this.canEditDescription(this.node);
    this.checkAndFetchNodeData();
    this.cdr.detectChanges();
  }

  private checkAndFetchNodeData(): void {
    if (this.node && this.displayTemplate !== 'root' && this.node._id !== this.previousNodeId) {
      this.previousNodeId = this.node._id;
      this.getRefModelNodeRelatedData();
    }
  }

  private canEditDescription(node: RefModelNode): boolean {
    return node.description.isUpdatable && this.selectedTabIndex === 0;
  }

  private getNodeType(node: RefModelNode, refModel: SalesForceData): string {
    let typeOfNode = 'root';

    const isRefModel = node?._id === refModel._id;
    const refShow = !node?.sf && !isRefModel;
    const isPro = this.permissionsService.isProSpace();
    const result = isPro && refShow;

    if (result) {
      typeOfNode = 'node';
    }

    if (isRefModel) {
      typeOfNode = 'root';
    }

    return typeOfNode;
  }

  public deleteNode(node: RefModelNode): void {
    const data: any = {
      internalNode: true,
      nodeId: node.identity,
      nodeName: node.name,
      refModelId: node.refModel
    };

    this.deleteNodeDialogService
      .getDeleteNodeDialog(data)
      .afterClosed()
      .pipe(filter(Boolean))
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe(result => {
        if (result._id === data.nodeId) {
          return this.router.navigate(['/refmodel', this.refModel._id]);
        }

        return this.nodeRemoveEmitterService.emitNodeRemoved(data);
      });
  }

  public editNode(node: RefModelNode) {
    const nodePropertiesToUpdate = [
      {property: 'description', value: node.description},
      {property: 'customId', value: node.customId},
      {property: 'name', value: node.name}
    ];

    this.internalDialogService
      .openEditNode(node)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(result => {
        this.refModelSidebarService.refModelNode.set(result);

        nodePropertiesToUpdate.forEach(({property, value}) => {
          this.updateTreeService.set('updateNodeInTree', {
            value,
            property,
            nodeId: result._id
          });
        });
      });
  }

  private getUserRefModels() {
    forkJoin([
      this.refModelSidebarDaoService.getRefModelsList(true),
      this.refModelSidebarDaoService.getRefModelsList(false, 50)
    ])
      .pipe(take(1))
      .subscribe(([internalRMs, externalRMs]: [RefModelList, RefModelList]) => {
        const userRefModelIds = [...(internalRMs.list || []), ...(externalRMs.list || [])]
          .filter(refModel => refModel.access || refModel.viewAll)
          .map(refModelsWithAccess => refModelsWithAccess.id);

        this.refModelSidebarService.userRefModelsList.set(userRefModelIds);
      });
  }

  private getRefModelNodeRelatedData() {
    if (this.displayTemplate === 'root') {
      return;
    }

    const refModelId = this.refModelSidebarService.refModelId();
    const nodeId = this.refModelSidebarService.refModelNodeId();

    this.loading.set(true);

    this.refModelNotesDaoService.setBaseUrl({refModelId, nodeId});
    this.refModelSidebarService.calculateFeedbackCount();

    forkJoin([
      this.refModelAttachmentDaoService.fetchAttachmentsByNode(refModelId, nodeId),
      this.refModelSidebarService.getStakeholders(),
      this.dependenciesService.getDependenciesByNode()
    ])
      .pipe(
        this.takeUntilComponentDestroy(),
        finalize(() => this.loading.set(false))
      )
      .subscribe(([refModelNodeAttachments, stakeholders, deps]) => {
        this.dependenciesData.set({
          parents: deps.parents.parents,
          children: deps.children.children
        });

        const canPasteOrg = this.canPasteOrgModelNodeWithCookieKey(
          constants.cookie.copiedSFNodeKey,
          this.dependenciesData()
        );
        const canPasteRef = this.canPasteOrgModelNodeWithCookieKey(
          constants.cookie.copiedNodeKey,
          this.dependenciesData()
        );

        this.rmeStore.set(REF_MODEL_STORE.CAN_PASTE_ORG_NODE, canPasteOrg);
        this.rmeStore.set(REF_MODEL_STORE.CAN_PASTE_REF_NODE, canPasteRef);

        this.refModelSidebarService.refModelNodeAttachments.set(refModelNodeAttachments);
        this.refModelSidebarService.stakeholdersList.set(stakeholders);
      });
  }

  public canPasteOrgModelNodeWithCookieKey(cookieKey: string, dependenciesData) {
    return this.dependenciesService.canPasteOrgModelNode(dependenciesData, cookieKey);
  }

  public makeNodeRoot() {
    this.makeNodeRootClicked.emit();
  }

  public disableMakeNodeRoot(): boolean {
    return !this.node || this.node?._id === this.rmeStore.rootData?._id;
  }
}
